import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CreateCrmContactTaskPayload, CrmTask, TaskQuickFilter, TaskStatus } from "types/crm/task";
import { usePaginatedList } from "../usePaginatedList";
import { contactApi, contactQueryKeys, UpdateCrmContactTaskPayload } from "service/crm/api/contact";
import { useEntityCreateMutation } from "../useEntityMutation";


export const useContactTasks = (
  contactUuid: string,
  pageSize = 20,
  status?: TaskStatus,
  quickFilter?: TaskQuickFilter,
) => {
  const { items, ...rest } = usePaginatedList<CrmTask>(
    contactQueryKeys.tasks(contactUuid, status, quickFilter),
    (page) => contactApi.getContactTasks(contactUuid, page, pageSize, status, quickFilter),
    !!contactUuid,
  );
  return { tasks: items, ...rest };
};

/** This contact's tasks that are past due and not completed. */
export const useContactOverdueTasks = (contactUuid: string, pageSize = 20) =>
  useContactTasks(contactUuid, pageSize, undefined, "OVERDUE");

export const useCreateTaskMutation = () =>
  useEntityCreateMutation<CreateCrmContactTaskPayload, CrmTask>(
    (payload) => contactApi.createContactTask(payload),
    (variables) => [
      [...contactQueryKeys.all, "tasks", variables.contactUuid],
      contactQueryKeys.activities(variables.contactUuid),
    ],
  );

export const useUpdateContactTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateCrmContactTaskPayload) =>
      contactApi.updateContactTask(payload),
    onSuccess: (_data, variables) => {
      // Prefix-match invalidation: clears the cache for every status filter
      // of this contact's task list, not just "ALL".
      queryClient.invalidateQueries({
        queryKey: [...contactQueryKeys.all, "tasks", variables.contactUuid],
      });
      queryClient.invalidateQueries({
        queryKey: contactQueryKeys.activities(variables.contactUuid),
      });
    },
  });
};

export const useDeleteContactTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contactUuid,
      taskUuid,
    }: {
      contactUuid: string;
      taskUuid: string;
    }) => contactApi.deleteContactTask(contactUuid, taskUuid),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...contactQueryKeys.all, "tasks", variables.contactUuid],
      });
      queryClient.invalidateQueries({
        queryKey: contactQueryKeys.activities(variables.contactUuid),
      });
    },
  });
};